import '@src/modules/shared/ui/infrastructure/drivers/ui/components/LoadingComponent.ts';
import type { CatalogDownloadPort } from '@src/modules/sync/domain/ports/CatalogDownloadPort.ts';
import type { CatalogDownloadProgress } from '@src/modules/sync/domain/ports/CatalogDownloadPort.ts';
import type { MasterDataApplyCounts } from '@src/modules/sync/domain/ports/CatalogDownloadPort.ts';
import { StartupSyncOrchestrator, type SyncState } from '@src/modules/sync/application/StartupSyncOrchestrator.ts';
import type { StartupCatalogSyncOutcome } from '@src/modules/sync/application/startupCatalogSyncOutcome.ts';
import { isSnapshotRevisionUpgradePending } from '@src/modules/shared/local-sqlite/infrastructure/snapshotRevisionGuard.ts';
import { acknowledgeSnapshotRevisionCatalogRefresh } from '@src/modules/shared/bootstrap/infrastructure/postSnapshotRevisionUpgradeNotify.ts';
import {
  catalogDownloadHasVisibleProgress,
  catalogDownloadProgressDetail,
  catalogDownloadProgressPercent,
} from '@src/modules/sync/infrastructure/drivers/page/catalogDownloadProgressUi.ts';
import { SYNC_CATALOG_POST_AUTH_DOWNLOAD_TIMEOUT_MS } from '@src/modules/sync/infrastructure/driven/syncCatalogTimeouts.ts';
import { fail, type Result } from '@core/domain/utils/Result.ts';

const STARTUP_OVERLAY_MESSAGES: Record<SyncState, string> = {
  CHECKING_NETWORK: 'Validando conexión...',
  CHECKING_UPDATES: 'Buscando actualizaciones...',
  READY: '',
};

const POST_AUTH_OVERLAY_MESSAGE = 'Actualizando catálogos tras iniciar sesión...';

function overlayMessageForState(state: SyncState): string {
  if (state === 'CHECKING_UPDATES' && isSnapshotRevisionUpgradePending()) {
    return 'Actualizando catálogos tras nueva versión de la app...';
  }
  return STARTUP_OVERLAY_MESSAGES[state];
}

type LoadingElement = HTMLElement & {
  setProps?: (p: {
    message?: string;
    progress?: { loaded: number; total: number; percent: number | null; detail: string } | null;
  }) => void;
  style: CSSStyleDeclaration;
};

type CatalogSyncOverlayUi = {
  setMessage: (message: string) => void;
  setCatalogProgress: (progress: CatalogDownloadProgress) => void;
  close: () => void;
};

function patchLoadingEl(
  loadingEl: LoadingElement,
  props: {
    message?: string;
    progress?: { loaded: number; total: number; percent: number | null; detail: string } | null;
  },
): void {
  if (typeof loadingEl.setProps === 'function') {
    loadingEl.setProps(props);
    return;
  }
  customElements.whenDefined('loading-component').then(() => {
    loadingEl.setProps?.(props);
  });
}

function mountCatalogSyncOverlay(initialMessage: string): CatalogSyncOverlayUi {
  const overlay = document.createElement('div');
  overlay.style.position = 'fixed';
  overlay.style.inset = '0';
  overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
  overlay.style.zIndex = '9999';
  overlay.style.display = 'flex';
  overlay.style.alignItems = 'center';
  overlay.style.justifyContent = 'center';

  const loadingEl = document.createElement('loading-component') as LoadingElement;
  loadingEl.style.backgroundColor = 'var(--md-sys-color-surface, #fff)';
  loadingEl.style.padding = '24px';
  loadingEl.style.borderRadius = '12px';
  loadingEl.style.minWidth = '18rem';
  loadingEl.style.boxShadow =
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';

  overlay.appendChild(loadingEl);
  document.body.appendChild(overlay);

  const setMessage = (message: string): void => {
    if (!message) return;
    patchLoadingEl(loadingEl, { message });
  };

  const setCatalogProgress = (progress: CatalogDownloadProgress): void => {
    if (!catalogDownloadHasVisibleProgress(progress)) return;
    const message =
      progress.phase === 'downloading'
        ? 'Descargando catálogos...'
        : 'Aplicando catálogos en el dispositivo...';
    patchLoadingEl(loadingEl, {
      message,
      progress: {
        loaded: progress.loaded,
        total: progress.total,
        percent: catalogDownloadProgressPercent(progress),
        detail: catalogDownloadProgressDetail(progress),
      },
    });
  };

  patchLoadingEl(loadingEl, { message: initialMessage });

  return {
    setMessage,
    setCatalogProgress,
    close: () => {
      patchLoadingEl(loadingEl, { progress: null });
      overlay.remove();
    },
  };
}

async function withDownloadTimeout<T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout>;
  const timeoutPromise = new Promise<T>((resolve) => {
    timeoutId = setTimeout(() => resolve(fallback), ms);
  });
  return Promise.race([promise.finally(() => clearTimeout(timeoutId)), timeoutPromise]);
}

/** Full-screen overlay while startup catalog sync runs (cold boot only). */
export async function runStartupSyncOverlay(
  catalogDownload: CatalogDownloadPort | null | undefined,
): Promise<StartupCatalogSyncOutcome | null> {
  const ui = mountCatalogSyncOverlay(STARTUP_OVERLAY_MESSAGES.CHECKING_NETWORK);

  try {
    if (catalogDownload) {
      const orchestrator = new StartupSyncOrchestrator(catalogDownload, undefined, {
        isRevisionUpgradePending: isSnapshotRevisionUpgradePending,
        onCatalogDownloadSuccess: acknowledgeSnapshotRevisionCatalogRefresh,
      });
      return await orchestrator.run(
        (state) => {
          if (state === 'READY') {
            ui.close();
            return;
          }
          ui.setMessage(overlayMessageForState(state));
        },
        (progress) => {
          ui.setCatalogProgress(progress);
        },
      );
    }
    return { status: 'unavailable' };
  } finally {
    ui.close();
  }
}

/** Full-screen overlay for post-auth catalog retry (session now has bearer token). */
export async function runPostAuthCatalogSyncOverlay(
  catalogDownload: CatalogDownloadPort,
): Promise<Result<MasterDataApplyCounts, string>> {
  const ui = mountCatalogSyncOverlay(POST_AUTH_OVERLAY_MESSAGE);

  try {
    const outcome = await withDownloadTimeout(
      catalogDownload.runDownload((progress) => {
        ui.setCatalogProgress(progress);
      }),
      SYNC_CATALOG_POST_AUTH_DOWNLOAD_TIMEOUT_MS,
      null,
    );

    if (outcome == null) {
      console.warn(
        `[StartupSync] Post-auth catalog download timed out after ${SYNC_CATALOG_POST_AUTH_DOWNLOAD_TIMEOUT_MS}ms.`,
      );
      return fail(
        `La descarga de catálogos superó el tiempo límite (${Math.round(SYNC_CATALOG_POST_AUTH_DOWNLOAD_TIMEOUT_MS / 1000)} s). Intente Sync → Descargar ahora.`,
      );
    }

    return outcome;
  } finally {
    ui.close();
  }
}
